'use client';

import { forwardRef, useCallback, useState } from 'react';

// --- Icons ---
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import { HEADING_SHORTCUT_KEYS, HeadingButton } from '@/components/tiptap-editor/tiptap-ui/heading-button';
import type { UseHeadingDropdownMenuConfig } from '@/components/tiptap-editor/tiptap-ui/heading-dropdown-menu';
import { useHeadingDropdownMenu } from '@/components/tiptap-editor/tiptap-ui/heading-dropdown-menu';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';
import { ButtonGroup } from '../../tiptap-ui-primitive/button-group';

export interface HeadingDropdownMenuProps extends Omit<ButtonProps, 'type'>, UseHeadingDropdownMenuConfig {
    /**
     * Whether to render the dropdown menu in a portal
     * @default false
     */
    portal?: boolean;
    /**
     * Callback for when the dropdown opens or closes
     */
    onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Dropdown menu component for selecting heading levels in a Tiptap editor.
 *
 * For custom dropdown implementations, use the `useHeadingDropdownMenu` hook instead.
 */
export const HeadingDropdownMenu = forwardRef<HTMLButtonElement, HeadingDropdownMenuProps>(
    (
        { editor: providedEditor, levels = [1, 2, 3, 4, 5, 6], hideWhenUnavailable = false, portal = true, onOpenChange, ...buttonProps },
        ref,
    ) => {
        const { editor } = useTiptapEditor(providedEditor);
        const [isOpen, setIsOpen] = useState<boolean>(false);
        const { isVisible, isActive, canToggle, Icon } = useHeadingDropdownMenu({
            editor,
            levels,
            hideWhenUnavailable,
        });

        const handleOpenChange = useCallback(
            (open: boolean) => {
                if (!editor || !canToggle) return;
                setIsOpen(open);
                onOpenChange?.(open);
            },
            [canToggle, editor, onOpenChange],
        );

        if (!isVisible) {
            return null;
        }

        return (
            <DropdownMenu
                modal
                open={isOpen}
                onOpenChange={handleOpenChange}
            >
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        data-style="ghost"
                        data-active-state={isActive ? 'on' : 'off'}
                        role="button"
                        tabIndex={-1}
                        disabled={!canToggle}
                        data-disabled={!canToggle}
                        aria-label="Format text as heading"
                        aria-pressed={isActive}
                        tooltip="Heading"
                        {...buttonProps}
                        ref={ref}
                    >
                        <Icon className="tiptap-button-icon" />
                        <ChevronDownIcon className="tiptap-button-dropdown-small" />
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                    align="start"
                    portal={portal}
                >
                    <Card>
                        <CardBody>
                            <ButtonGroup>
                                {levels.map(level => (
                                    <DropdownMenuItem
                                        key={`heading-${level}`}
                                        asChild
                                    >
                                        <HeadingButton
                                            editor={editor}
                                            level={level}
                                            text={`Heading ${level}`}
                                            shortcutKeys={HEADING_SHORTCUT_KEYS[level]}
                                        />
                                    </DropdownMenuItem>
                                ))}
                            </ButtonGroup>
                        </CardBody>
                    </Card>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    },
);

HeadingDropdownMenu.displayName = 'HeadingDropdownMenu';

export default HeadingDropdownMenu;
